import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert, Paper, Skeleton, Stack, Text, Title } from "@mantine/core";
import { useMaintenanceRuns } from "../api/maintenance";
import { Health, ParserBoard, Repairs } from "./MaintenanceChecks";
import { HoldsPanel } from "./MaintenanceHolds";
import { FINISHED, MONO, RunDetail, RunTable } from "./MaintenanceRun";
import { behindCount } from "./reparse";
import { DevelopmentGatePanel } from "./DevelopmentGatePanel";

/**
 * Maintenance: repairs an operator asks for, and switches that turn things off for a while.
 *
 * Every repair here is two steps, and that is not a confirmation dialog. Previewing works out
 * exactly which rows would change and writes that down; applying carries out that written
 * plan and nothing else. So what gets approved is a specific set of changes rather than a
 * number -- and if the parser moves in between, the plan is refused rather than quietly
 * meaning something different from what was read.
 */
export function MaintenancePage() {
  const { data, isLoading } = useMaintenanceRuns();
  const { t } = useTranslation("admin");

  // Follow whatever is moving, so pressing Preview lands you on its result without a click.
  const activeId = data?.runs.find((run) => !FINISHED.has(run.state))?.id;
  // A row opened by hand is tagged with the job that was moving when it was picked, so a new
  // one starting takes the panel back while the end of one leaves the panel where it was.
  const [picked, setPicked] = useState<{ against: number | undefined; id: number | null } | null>(
    null,
  );
  const setOpenRun = (id: number | null) => setPicked({ against: activeId, id });
  const openRun =
    activeId !== undefined && picked?.against !== activeId ? activeId : (picked?.id ?? null);

  const behind = data ? behindCount(data.parser_coverage, data.fingerprint) : 0;

  return (
    <Paper p="md" m={20} style={{ overflowY: "auto", minHeight: 0 }}>
      <Title order={2} fz="lg" lh={1.5} mb={6}>
        {t("maintenancePage.heading")}
      </Title>
      <Text size="xs" c="dimmed" maw="70ch" mb={14}>
        {t("maintenancePage.blurb")}
      </Text>

      <DevelopmentGatePanel />

      {/* Why the buttons are off, in the server's own words. Inferring the reason in the
          client is how the UI ends up confidently stating something the server never said. */}
      {data?.blocked_reason && (
        <Alert color="yellow" variant="light" mb={14}>
          {t("maintenancePage.currentlyBlocked", { reason: data.blocked_reason })}
        </Alert>
      )}

      <Health blocked={data?.blocked_reason ?? null} onStarted={setOpenRun} />

      <Repairs blocked={data?.blocked_reason ?? null} onStarted={setOpenRun} />

      {openRun !== null && <RunDetail runId={openRun} />}

      <ParserBoard />

      <HoldsPanel />

      <Title order={3} fz="sm" lh={1.5} mb={4}>
        {t("maintenancePage.historyHeading")}
      </Title>
      {isLoading ? (
        <Stack gap={6}>
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} height={20} />
          ))}
        </Stack>
      ) : (
        <RunTable runs={data?.runs ?? []} openRun={openRun} onOpen={setOpenRun} />
      )}

      {data && (
        <Text size="xs" c="dimmed" mt={20}>
          <Trans
            ns="admin"
            i18nKey="maintenancePage.fingerprint"
            values={{ fingerprint: data.fingerprint }}
            components={{ code: <Text component="code" ff={MONO} fz="inherit" /> }}
          />
          {behind > 0 && (
            <>
              {" "}
              <Trans
                ns="admin"
                i18nKey="maintenancePage.behindParser"
                count={behind}
                components={{ strong: <strong /> }}
              />
            </>
          )}
        </Text>
      )}
    </Paper>
  );
}
